import { useEffect, useState } from "react";
import type { BadgeDefinitionResponse } from "@/features/badges/api";
import { getAllBadgeDefinitions } from "@/features/badges/api";
import { COMPETITION_BADGE_COUNT, pickTieredBadges } from "@/server/data/badges";
import type { CompetitionType } from "@/types";

export type BadgeMode = "auto" | "manual";

export const COMP_TYPE_LABELS: Record<CompetitionType, { label: string; desc: string }> = {
  step_race: { label: "Step race", desc: "Most total steps in the date range wins" },
  target_race: { label: "Target race", desc: "First to reach a step target wins" },
  daily_goals: { label: "Daily goals", desc: "Most days hitting your daily step goal wins" },
};

function toggleBadge(current: string[], badgeId: string): string[] {
  if (current.includes(badgeId)) return current.filter((id) => id !== badgeId);
  if (current.length >= COMPETITION_BADGE_COUNT) return current;
  return [...current, badgeId];
}

function computeAutoBadgeIds(badges: BadgeDefinitionResponse[]): string[] {
  return pickTieredBadges(
    badges.map((b) => ({
      id: b.badgeId,
      name: b.name,
      description: b.description,
      iconEmoji: b.iconEmoji,
      stepThreshold: b.stepThreshold,
      tier: b.tier,
    })),
  ).map((b) => b.id);
}

export function useCompetitionForm() {
  const [allBadges, setAllBadges] = useState<BadgeDefinitionResponse[]>([]);
  const [autoBadgeIds, setAutoBadgeIds] = useState<string[]>([]);
  const [badgeMode, setBadgeMode] = useState<BadgeMode>("auto");
  const [selectedBadgeIds, setSelectedBadgeIds] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await getAllBadgeDefinitions();
      if (data) {
        const sorted = data.sort((a, b) => a.stepThreshold - b.stepThreshold);
        setAllBadges(sorted);
        setAutoBadgeIds(computeAutoBadgeIds(sorted));
      }
    })();
  }, []);

  function handleShuffle(): void {
    setAutoBadgeIds(computeAutoBadgeIds(allBadges));
  }

  const autoBadges: BadgeDefinitionResponse[] = autoBadgeIds
    .map((id) => allBadges.find((b) => b.badgeId === id))
    .filter((b): b is BadgeDefinitionResponse => b != null);

  const sortedAllBadges = [...allBadges].sort((a, b) => a.stepThreshold - b.stepThreshold);

  return {
    allBadges,
    autoBadgeIds,
    badgeMode,
    setBadgeMode,
    selectedBadgeIds,
    setSelectedBadgeIds,
    toggleBadge,
    handleShuffle,
    autoBadges,
    sortedAllBadges,
  };
}
